import React, { useState, useEffect } from "react";
import { ReactComponent as ChevronDownIcon } from "assets/images/chevron-down.svg";
import { ReactComponent as ChevronRightIcon } from "assets/images/chevron16.svg";
import {
  useGrantViewerAccessMutation,
  useViewerAccessesQuery,
  useRevokeViewerAccessMutation,
} from "generated/graphql";
import { useToast } from "hooks/Toast";
import {
  Container,
  Description,
  FormSection,
  InputContainer,
  Label,
  InputWrapper,
  Input,
  Divider,
  InvitedSection,
  InvitedTitle,
  TableSection,
  TableContainer,
  TableHeader,
  TableHeaderItem,
  TableRow,
  TableRowEmail,
  TableRowStatus,
  StatusContainer,
  StatusText,
  EmptyState,
  EmptyStateText,
  SendButton,
  DropdownWrapper,
  DropdownMenu,
  DropdownItem,
  ClickableChevronContainer,
} from "./styles";

const ViewerAccessModalContent = ({
  testId,
  onClose,
}: {
  testId: string;
  onClose: () => void;
}) => {
  const [email, setEmail] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [openDropdownEmail, setOpenDropdownEmail] = useState<string | null>(
    null
  );

  const [grantViewerAccess] = useGrantViewerAccessMutation();
  const [revokeViewerAccess] = useRevokeViewerAccessMutation();
  const Toast = useToast();

  const {
    data: viewerAccesses,
    refetch: refetchViewerAccesses,
  } = useViewerAccessesQuery();

  const handleSendInvite = () => {
    grantViewerAccess({
      variables: {
        email,
        testId,
      },
    }).then(() => {
      refetchViewerAccesses();
      onClose();
    });
    setOpenDropdownEmail(null);
    setEmail("");
    Toast("success", "Invite sent successfully");
  };

  const handleRevokeAccess = async (granteeEmail: string) => {
    try {
      await revokeViewerAccess({
        variables: {
          email: granteeEmail,
          testId,
        },
        refetchQueries: ["ViewerAccesses"],
      });
      setOpenDropdownEmail(null);
      Toast("success", "Access revoked successfully");
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error revoking access:", error);
      Toast("error", "Failed to revoke access");
    }
  };

  const handleChevronClick = (
    granteeEmail: string,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    setOpenDropdownEmail(
      openDropdownEmail === granteeEmail ? null : granteeEmail
    );
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!openDropdownEmail) return;

    const handleClickOutside = (event: MouseEvent) => {
      // Check if the click is inside any dropdown
      const target = event.target as Element;
      if (target.closest("[data-dropdown-wrapper]")) {
        return; // Don't close if clicking inside dropdown
      }
      setOpenDropdownEmail(null);
    };

    // Add a small delay to prevent immediate closure from the same click
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 100);

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdownEmail]);

  const invitedCount = viewerAccesses?.viewerAccesses?.length || 0;

  const isEmailValid = email.includes("@") && email.includes(".");

  return (
    <Container>
      <Description>
        Invite people to view your dashboard - you can remove their access at
        any time.
      </Description>

      <FormSection>
        <InputContainer>
          <Label htmlFor="email">Email address</Label>
          <InputWrapper>
            <Input
              id="email"
              type="email"
              placeholder="We will send invitation to this email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </InputWrapper>
        </InputContainer>
      </FormSection>

      <Divider />

      <InvitedSection>
        <InvitedTitle onClick={() => setIsExpanded(!isExpanded)}>
          Invited people ({invitedCount})
        </InvitedTitle>

        <ChevronDownIcon
          style={{ transform: isExpanded ? "rotate(0deg)" : "rotate(180deg)" }}
        />
      </InvitedSection>

      <TableSection isExpanded={isExpanded}>
        <TableContainer>
          <TableHeader>
            <TableHeaderItem>Invited</TableHeaderItem>
            <TableHeaderItem>Status</TableHeaderItem>
          </TableHeader>
          {viewerAccesses?.viewerAccesses?.length === 0 ? (
            <EmptyState>
              <EmptyStateText>
                People who are invited to view your dashboard will appear here
              </EmptyStateText>
            </EmptyState>
          ) : (
            viewerAccesses?.viewerAccesses?.map((viewerAccess) => (
              <TableRow key={viewerAccess.granteeEmail}>
                <TableRowEmail>{viewerAccess.granteeEmail}</TableRowEmail>
                <TableRowStatus>
                  <StatusContainer>
                    {viewerAccess.revokedAt ? (
                      <StatusText
                        style={{
                          color: "#FF0000",
                          paddingRight: "25px",
                          fontSize: "14px",
                        }}
                      >
                        Revoked
                      </StatusText>
                    ) : (
                      <StatusText>Invited</StatusText>
                    )}
                  </StatusContainer>
                  {!viewerAccess.revokedAt && (
                    <DropdownWrapper data-dropdown-wrapper>
                      <ClickableChevronContainer
                        onClick={(e) =>
                          handleChevronClick(viewerAccess.granteeEmail!, e)
                        }
                      >
                        <ChevronRightIcon />
                      </ClickableChevronContainer>
                      <DropdownMenu
                        isOpen={openDropdownEmail === viewerAccess.granteeEmail}
                      >
                        <DropdownItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRevokeAccess(viewerAccess.granteeEmail!);
                          }}
                        >
                          Remove access
                        </DropdownItem>
                      </DropdownMenu>
                    </DropdownWrapper>
                  )}
                </TableRowStatus>
              </TableRow>
            ))
          )}
        </TableContainer>
      </TableSection>

      <SendButton
        onClick={handleSendInvite}
        disabled={!isEmailValid || !email.trim()}
      >
        Send Invite
      </SendButton>
    </Container>
  );
};

export default ViewerAccessModalContent;
